/**
 * A status report as a compact, ruled sheet.
 *
 * The slide is built to be projected, so at anything under about 1100px wide
 * its type is too small to read. This lays the same content out as a
 * document: a title strip saying whose report it is, one line of facts (the
 * RAGs, the stage, the go-live, the budget), the narrative, then only the
 * sections the project manager actually filled in. What was left empty is
 * named in one quiet line rather than given a heading each. The slide itself
 * is a click away for anyone who wants it exactly as filed.
 */

import { Link } from 'react-router-dom'
import { ArrowRight, Presentation } from 'lucide-react'
import { effectiveRag, monthLabel } from '../lib/report'
import { fmtUk } from '../lib/reportingWindow'
import { formatGoLive } from '../lib/ddi'
import { PROJECT_STAGES } from '../types'
import type { Milestone, MilestoneStatus, ProjectReport, RagAssessment, RagStatus } from '../types'

const RAG_DOT: Record<RagStatus, string> = { green: '#00843D', amber: '#F88806', red: '#EF3340' }
const RAG_TEXT: Record<RagStatus, string> = { green: '#00602C', amber: '#8B4900', red: '#9B1B26' }
const RAG_WORD: Record<RagStatus, string> = { green: 'Green', amber: 'Amber', red: 'Red' }

const MILESTONE_LABEL: Record<MilestoneStatus, string> = {
  planned: 'Planned', achieved: 'Achieved', at_risk: 'At risk', missed: 'Missed',
}
const MILESTONE_TONE: Record<MilestoneStatus, string> = {
  planned: 'text-slate-500', achieved: 'text-[#00602C]', at_risk: 'text-[#8B4900]', missed: 'text-[#9B1B26]',
}

const monthName = (month: string) => monthLabel(month).split(' ')[0]

/** "Unchanged since August", "Amber in August", "First report": how the
 *  overall RAG got to where it is. */
export function movementNote(report: ProjectReport, prevMonth: string): string {
  const prev = report.overallPrevious
  if (!prev) return 'First report'
  if (prev === report.overall) return `Unchanged since ${monthName(prevMonth)}`
  return `${RAG_WORD[prev]} in ${monthName(prevMonth)}`
}

/** "Submitted by Pat Manager, Fri 5 Sep" and the like, from the lifecycle
 *  stamps. A report filed before the stamps existed carries no name, so the
 *  line says what happened without inventing who did it. */
export function filedLine(report: ProjectReport | undefined): string {
  if (!report) return 'No status report filed'
  const by = (who?: { name?: string; email?: string }) => {
    const name = who?.name || who?.email
    return name ? ` by ${name}` : ''
  }
  const when = (at?: string) => (at ? `, ${fmtUk(new Date(at), false, false)}` : '')
  if (report.state === 'reviewed') return `Validated${by(report.reviewedBy)}${when(report.reviewedAt)}`
  if (report.state === 'submitted') return `Submitted${by(report.submittedBy)}${when(report.submittedAt)}`
  return 'Draft, not submitted yet'
}

/** "highlights, lowlights or milestones": a list as a sentence reads it. */
export function listed(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`
}

const money = (v?: number) => (v == null ? null : v >= 1_000_000 ? `€${(v / 1_000_000).toFixed(1)}Mn` : `€${Math.round(v / 1000)}k`)

// Milestone dates read as "5 Sep 2026": a weekday is noise in a table.
const dateOnly = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))

/** The small capitals every label on the sheet is set in. */
function Label({ children, tone = 'plain' }: { children: React.ReactNode; tone?: 'plain' | 'ask' }) {
  return (
    <span className={`text-[10px] font-bold tracking-[0.14em] uppercase ${tone === 'ask' ? 'text-[#B8640B]' : 'text-slate-500'}`}>
      {children}
    </span>
  )
}

/** One fact on the facts line. Every fact rules its right and bottom edge;
 *  the line pulls in by a pixel so the outer edges fall off. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 px-3 py-1.5 border-r border-b border-slate-200 min-w-0">
      <dt className="shrink-0"><Label>{label}</Label></dt>
      <dd className="flex items-baseline gap-1.5 flex-wrap text-sm min-w-0">{children}</dd>
    </div>
  )
}

function Rag({ rag }: { rag: RagStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-bold" style={{ color: RAG_TEXT[rag] }}>
      <span className="w-2 h-2 rounded-full" style={{ background: RAG_DOT[rag] }} />
      {RAG_WORD[rag]}
    </span>
  )
}

/** A RAG with its reason, but only where the reason earns its place: an
 *  amber or red needs explaining, a green does not, and an override always
 *  says so. */
function RagFact({ label, a }: { label: string; a: RagAssessment }) {
  const rag = effectiveRag(a)
  const note = a.override ? a.overrideReason : rag !== 'green' ? a.derivedReason : ''
  return (
    <Fact label={label}>
      <Rag rag={rag} />
      {a.override && <span className="text-[10px] text-slate-400">set by PM</span>}
      {note && <span className="text-xs text-slate-500">{note}</span>}
    </Fact>
  )
}

function Bullets({ items }: { items: { id: string; text: string }[] }) {
  return (
    <ul className="space-y-1 list-disc pl-4 marker:text-slate-300">
      {items.map(b => (
        <li key={b.id} className="text-sm text-slate-700 leading-snug pl-0.5">{b.text}</li>
      ))}
    </ul>
  )
}

function MilestoneTable({ milestones }: { milestones: Milestone[] }) {
  return (
    <table className="w-full text-sm">
      <tbody className="divide-y divide-slate-100">
        {milestones.map(m => (
          <tr key={m.id}>
            <td className={`py-0.5 pr-3 ${m.status === 'achieved' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{m.label}</td>
            <td className="py-0.5 pr-3 text-right text-slate-500 tabular-nums whitespace-nowrap">{m.date ? dateOnly(m.date) : ''}</td>
            <td className={`py-0.5 text-right text-xs font-semibold whitespace-nowrap ${MILESTONE_TONE[m.status]}`}>{MILESTONE_LABEL[m.status]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface Block {
  title: string
  tone?: 'plain' | 'ask'
  body: React.ReactNode
}

export default function ReportReader({ report, month, prevMonth, onViewSlide }: {
  report: ProjectReport
  month: string
  prevMonth: string
  onViewSlide: () => void
}) {
  const s = report.snapshot
  const stage = PROJECT_STAGES.find(st => st.key === s.stage)?.label
  const goLive = formatGoLive(s.goLive, report.datePrecision)
  const people = [
    ['Project manager', s.projectManager], ['Functional lead', s.functionalLead], ['VP', s.functionalVP],
  ].filter(([, v]) => v) as [string, string][]
  const budgetYear = s.budgetYear ?? month.slice(0, 4)
  const risks = s.risks ?? []
  // The headline is meant to be one line, and is set bold to read as one.
  // Some reports put a whole paragraph there, and a bold paragraph shouts:
  // past a line or so it is set as ordinary body text.
  const headlineIsShort = (report.whyLine?.length ?? 0) <= 120

  // Only what was filled in gets a heading. The rest is named once, below.
  const blocks: Block[] = []
  const empty: string[] = []
  const block = (title: string, filled: boolean, body: () => React.ReactNode, tone?: 'ask') =>
    filled ? blocks.push({ title, tone, body: body() }) : empty.push(title.toLowerCase())
  block('Highlights', report.highlights.length > 0, () => <Bullets items={report.highlights} />)
  block('Lowlights', report.lowlights.length > 0, () => <Bullets items={report.lowlights} />)
  block('Support needed', report.supportNeeded.length > 0, () => <Bullets items={report.supportNeeded} />, 'ask')
  block('Open risks', risks.length > 0, () => (
    <ul className="space-y-1">
      {risks.map(r => (
        <li key={r.id} className="text-sm text-slate-700 leading-snug flex items-start gap-2">
          <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ background: RAG_DOT[r.rag] }} />
          <span>{r.title}{r.owner ? <span className="text-slate-400"> · {r.owner}</span> : null}</span>
        </li>
      ))}
    </ul>
  ))
  block('Milestones', s.milestones.length > 0, () => <MilestoneTable milestones={s.milestones} />)

  const budget = [
    s.capex ? `Capex ${money(s.capex)}${s.capexCommitted != null ? ` (${money(s.capexCommitted)} committed)` : ''}` : '',
    s.opex ? `Opex ${money(s.opex)}${s.opexCommitted != null ? ` (${money(s.opexCommitted)} committed)` : ''}` : '',
  ].filter(Boolean).join(' · ')

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden text-slate-800">
      {/* Whose report this is */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-[#002677] text-white">
        <div className="min-w-0 flex items-baseline gap-x-2.5 gap-y-0.5 flex-wrap">
          <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#00ACED] whitespace-nowrap">From the status report</span>
          <span className="text-sm font-bold truncate">{s.projectName} · {monthLabel(month)}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onViewSlide}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold text-white/85 hover:text-white hover:bg-white/10">
            <Presentation className="w-3.5 h-3.5" /> View as slide
          </button>
          <Link to={`/projects/${report.projectId}/report?month=${month}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-white/85 hover:text-white hover:bg-white/10">
            Open the report <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* The facts, on one line where the width allows */}
      <div className="border-b border-slate-200 bg-slate-50/50 overflow-hidden">
        <dl className="flex flex-wrap -mr-px -mb-px">
          <Fact label="Overall">
            <Rag rag={report.overall} />
            <span className="text-xs text-slate-500">{movementNote(report, prevMonth)}</span>
          </Fact>
          <RagFact label="Scope" a={report.scope} />
          <RagFact label="Schedule" a={report.schedule} />
          <RagFact label="Cost" a={report.cost} />
          <RagFact label="Benefit" a={report.benefit} />
          {stage && <Fact label="Stage"><span className="font-semibold text-slate-700">{stage}</span></Fact>}
          <Fact label="Go live"><span className="font-semibold text-slate-700 tabular-nums">{goLive}</span></Fact>
          {budget && <Fact label={`Budget ${budgetYear}`}><span className="font-semibold text-slate-700 tabular-nums">{budget}</span></Fact>}
        </dl>
      </div>

      {/* What the PM wrote */}
      <section className="px-4 py-2.5">
        <Label>Report narrative</Label>
        <div className="mt-1">
          {report.whyLine && (
            <p className={`text-sm leading-snug ${headlineIsShort ? 'font-semibold text-slate-800' : 'text-slate-700 leading-relaxed'}`}>
              {report.whyLine}
            </p>
          )}
          {report.narrative && <p className={`text-sm text-slate-700 leading-relaxed whitespace-pre-line ${report.whyLine ? 'mt-1' : ''}`}>{report.narrative}</p>}
          {!report.whyLine && !report.narrative && <p className="text-sm text-slate-400">No narrative in this report.</p>}
        </div>
      </section>

      {/* Only the sections with something in them, two to a row */}
      {blocks.length > 0 && (
        <div className="grid md:grid-cols-2">
          {blocks.map((b, i) => {
            const lastAlone = i === blocks.length - 1 && i % 2 === 0
            return (
              <section key={b.title}
                       className={`px-4 py-2.5 min-w-0 border-t border-slate-200 ${lastAlone ? 'md:col-span-2' : i % 2 === 0 ? 'md:border-r' : ''}`}>
                <Label tone={b.tone}>{b.title}</Label>
                <div className="mt-1">{b.body}</div>
              </section>
            )
          })}
        </div>
      )}

      {(empty.length > 0 || people.length > 0) && (
        <div className="border-t border-slate-200 px-4 py-1.5 text-xs text-slate-400 flex items-baseline justify-between gap-x-4 gap-y-0.5 flex-wrap">
          <span>{empty.length > 0 ? `Nothing filed under ${listed(empty)}.` : ''}</span>
          {people.length > 0 && (
            <span className="flex flex-wrap gap-x-3">
              {people.map(([k, v]) => <span key={k}>{k} <span className="text-slate-600">{v}</span></span>)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
